import { env } from "@monorepo/env/dashboard";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { experienceApi } from "@/lib/api";
import { useSession } from "@/lib/auth-client";
import { isHttpError } from "@/lib/http-error";

function loadPylonScript(appId: string) {
  if (document.querySelector(`script[src*="widget.usepylon.com"]`)) {
    return;
  }

  const script = document.createElement("script");
  script.setAttribute("type", "text/javascript");
  script.setAttribute("async", "true");
  script.setAttribute("src", `https://widget.usepylon.com/widget/${appId}`);
  document.head.appendChild(script);
}

const fetchPylonHash = async (): Promise<string | null> => {
  try {
    const { data } = await experienceApi.get<{ emailHash: string | null }>(
      "/api/config/pylon-hash"
    );
    return data.emailHash;
  } catch (error) {
    if (isHttpError(error)) {
      return null;
    }

    throw error;
  }
};

export const PylonChat = () => {
  const { data: session } = useSession();
  const user = session?.user;

  const { data: emailHash } = useQuery({
    queryKey: ["pylon-hash"],
    queryFn: fetchPylonHash,
    enabled: !!user && !!env.VITE_PYLON_APP_ID,
    staleTime: Number.POSITIVE_INFINITY,
  });

  useEffect(() => {
    if (!(env.VITE_PYLON_APP_ID && user)) {
      return;
    }

    // Don't initialize the widget without a verified identity — without
    // email_hash any user can spoof their email and impersonate others.
    if (!emailHash) {
      return;
    }

    window.pylon = {
      chat_settings: {
        app_id: env.VITE_PYLON_APP_ID,
        email: user.email ?? "",
        name: user.name,
        email_hash: emailHash,
      },
    };

    loadPylonScript(env.VITE_PYLON_APP_ID);
  }, [user, emailHash]);

  return null;
};
